import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AlertSubscriptionList from '../components/AlertCenter/AlertSubscriptionList';
import AlertSubscriptionKPIs from '../components/AlertCenter/AlertSubscriptionKPIs';
import AlertSubscriptionInfoModal from '../components/AlertCenter/AlertSubscriptionInfoModal';
import inputSignalsService from 'services/inputSignalsService';
import useInputSignalRules from 'hooks/useInputSignalRules';

const AlertCenter = () => {
	const { tenantId } = useParams();
	const navigate = useNavigate();

	const [showExplanationModal, setShowExplanationModal] = useState(false);
	const [inputSignalStatsLoading, setInputSignalStatsLoading] = useState(true);
	const [alertsReceivedCount, setAlertsReceivedCount] = useState(null);

	const {
		subscribedAlerts,
		allAlerts,
		loading,
		filter,
		setFilter,
		error,
		subscribe,
		unsubscribe,
	} = useInputSignalRules();

	// Fetch input signal stats for KPI card
	useEffect(() => {
		const fetchInputSignalStats = async () => {
			setInputSignalStatsLoading(true);
			try {
				const response = await inputSignalsService.getInputSignalsStats();
				setAlertsReceivedCount(
					response?.alerts_received_last_30_days_count || 0
				);
			} catch (error) {
				console.error('Failed to fetch input signal stats:', error);
			} finally {
				setInputSignalStatsLoading(false);
			}
		};

		fetchInputSignalStats();
	}, [tenantId, alertsReceivedCount]);

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				overflowY: 'auto',
				backgroundColor: 'background.default',
				padding: '12px 20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			{/* page header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'baseline',
					justifyContent: 'space-between',
				}}
			>
				<Box>
					<Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
						Alert Center
					</Typography>
					<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
						Your Alert Subscriptions within the Investor Engagement Manager
					</Typography>
				</Box>

				<Box
					sx={{
						display: 'flex',
						gap: 2,
					}}
				>
					<Button
						onClick={() => setShowExplanationModal(true)}
						variant='contained'
						sx={{
							backgroundColor: 'status.blue.bg',
							color: 'status.blue.text',
							border: '0.5px solid',
							borderColor: 'primary.main',
							boxShadow: 'none',
							'&:hover': {
								backgroundColor: 'status.blue.hover',
								boxShadow: 'none',
							},
						}}
					>
						Explain alert subscriptions
					</Button>
					<Button
						variant='contained'
						startIcon={<AddIcon />}
						onClick={() =>
							navigate(`/tenants/${tenantId}/workstation/alert_center/new`)
						}
					>
						New Alert
					</Button>
				</Box>
			</Box>

			{/* activity summary */}
			<Box>
				<AlertSubscriptionKPIs
					subscribedAlerts={subscribedAlerts}
					loading={loading || inputSignalStatsLoading}
					alertsReceivedCount={alertsReceivedCount}
				/>
			</Box>

			{/* alert list container  */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
				}}
			>
				<AlertSubscriptionList
					subscribedAlerts={subscribedAlerts}
					allAlerts={allAlerts}
					loading={loading || inputSignalStatsLoading}
					subscribe={subscribe}
					unsubscribe={unsubscribe}
					filter={filter}
					setFilter={setFilter}
					error={error}
				/>
			</Box>

			<AlertSubscriptionInfoModal
				open={showExplanationModal}
				onClose={() => setShowExplanationModal(false)}
			/>
		</Box>
	);
};

export default AlertCenter;
